import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Button,
  Container,
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import {
  APP_SHELL_LAYOUT,
} from "../../../constants/constants";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signUpUser,
  getFriendlyErrorMessage,
} from "../../../services/authService";
import { getOrCreateUserProfile } from "../../../services/profileService";
import useUserProfileStore, {
  selectSetUserProfile,
} from "../../../store/useUserProfileStore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AppBuildFooter from "../../shared/AppBuildFooter";

// Firestore / Auth
import { db } from "../../../firebase";
import {
  collection,
  getCountFromServer,
  doc,
  updateDoc,
  query,
  where,
  getDoc,
} from "firebase/firestore";

const SIGNIN_REDIRECT_KEY = "postAuthRedirectTo";

const normalizeFromTarget = (fromValue) => {
  if (!fromValue) return null;
  if (typeof fromValue === "string") return fromValue;
  if (typeof fromValue === "object") {
    const pathname = fromValue.pathname || "";
    const search = fromValue.search || "";
    const combined = `${pathname}${search}`;
    return combined || null;
  }
  return null;
};

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Return path after onboarding
  const queryParams = new URLSearchParams(location.search);
  const nextFromUrl = queryParams.get("next");
  const fromLocation = location.state?.from;
  const fromStatePath = normalizeFromTarget(fromLocation);
  const savedFrom = sessionStorage.getItem(SIGNIN_REDIRECT_KEY);
  const from = nextFromUrl || savedFrom || fromStatePath || "/";

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Global/app state
  const setUserProfile = useUserProfileStore(selectSetUserProfile);

  // Flow state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Admin config state (DB is source of truth)
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  // Capacity state (Inssa-only)
  const [userCount, setUserCount] = useState(null);
  const [countLoading, setCountLoading] = useState(true);
  const [countError, setCountError] = useState(null);

  // Load admin config (no fallbacks)
  useEffect(() => {
    const fetchConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const ref = doc(db, "config", "app");
        const snap = await getDoc(ref);
        setConfig(snap.exists() ? snap.data() || {} : {});
      } catch (e) {
        console.error("Error loading admin config:", e);
        setConfigError("Unable to load admin settings at the moment.");
        setConfig({});
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const resetScrollToTop = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document?.documentElement) document.documentElement.scrollTop = 0;
      if (document?.body) document.body.scrollTop = 0;
      const rootEl = document.getElementById("root");
      if (rootEl && typeof rootEl.scrollTo === "function") {
        rootEl.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    } catch (error) {
      console.warn("[signup] failed to reset scroll position:", error);
    }
  };

  useLayoutEffect(() => {
    resetScrollToTop();
    requestAnimationFrame(() => resetScrollToTop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Inssa-only user count
  useEffect(() => {
    const fetchUserCount = async () => {
      setCountLoading(true);
      setCountError(null);
      try {
        const coll = collection(db, "users");
        const q = query(coll, where("hasSignedUpWithINSSA", "==", true));
        const snapshot = await getCountFromServer(q);
        const count = snapshot.data().count || 0;
        setUserCount(count);
      } catch (e) {
        console.error("Error fetching Inssa users count:", e);
        setCountError("Unable to verify capacity at the moment.");
      } finally {
        setCountLoading(false);
      }
    };
    fetchUserCount();
  }, []);

  // Gate calc (DB-only)
  const alphaEnabled = !!config?.alphaLimitEnabled;
  const limit =
    typeof config?.alphaLimitMaxUsers === "number"
      ? config.alphaLimitMaxUsers
      : undefined;

  const overLimit =
    alphaEnabled &&
    typeof userCount === "number" &&
    typeof limit === "number" &&
    userCount >= Number(limit);

  const handleSignUp = async () => {
    setLoading(true);
    setError(null);

    try {
      // Final capacity check right before creating the account (reduces races)
      if (alphaEnabled && typeof limit === "number") {
        try {
          const coll = collection(db, "users");
          const q = query(coll, where("hasSignedUpWithINSSA", "==", true));
          const snapshot = await getCountFromServer(q);
          const latest = snapshot.data().count || 0;

          if (latest >= Number(limit)) {
            setError(
              config?.alphaLimitReachedMessage ||
                "Sign-ups are currently closed for the alpha."
            );
            setSnackbarOpen(true);
            return;
          }
        } catch (e) {
          // Fail closed for signups if we can’t verify (safer)
          setError("We can’t verify capacity right now. Please try again later.");
          setSnackbarOpen(true);
          return;
        }
      }

      // Create auth user
      const user = await signUpUser(email, password);
      console.info("[signup] firebase auth user created uid:", user?.uid, "email:", user?.email);

      // Always create/patch Firestore profile (handles both missing and partial docs)
      console.info("[signup] bootstrapping Firestore profile for uid:", user?.uid);
      const profile = await getOrCreateUserProfile(user);
      console.info("[signup] Firestore profile ready uid:", user?.uid, "hasProfile:", !!profile);

      // Ensure INSSA flag is set for future counts
      try {
        if (!profile?.hasSignedUpWithINSSA) {
          const userDocRef = doc(db, "users", user.uid);
          await updateDoc(userDocRef, { hasSignedUpWithINSSA: true });
          profile.hasSignedUpWithINSSA = true;
        }
      } catch (e) {
        console.warn("Failed to set hasSignedUpWithINSSA flag on signup:", e);
      }

      // Update state
      setUserProfile(profile);

      navigate(from, { replace: true });

      try {
        sessionStorage.removeItem(SIGNIN_REDIRECT_KEY);
      } catch {}
    } catch (err) {
      console.error("Error signing up:", err.message);
      setError(getFriendlyErrorMessage(err.code));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Loading while we check config and capacity
  if (countLoading || configLoading) {
    return (
      <>
        <Backdrop
          sx={(t) => ({ color: t.colors.white, zIndex: t.zIndex.drawer + 1 })}
          open
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Preparing sign up…
            </Typography>
          </Box>
        </Backdrop>
        <Container
          maxWidth="xs"
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            minHeight: "100%",
          })}
        />
      </>
    );
  }

  // If over capacity (and gate enabled), hide form and show guidance
  if (overLimit) {
    return (
      <>
        <Container
          maxWidth="xs"
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            textAlign: "center",
          })}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Alpha capacity reached
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {config?.alphaLimitReachedMessage ||
                "Sign-ups are currently closed for the alpha."}
            </Typography>
            <Stack direction="column" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={() => navigate("/signin", { state: { from } })}
                sx={{
                  p: "10px 20px",
                }}
              >
                I already have an account
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/", { replace: true })}
                sx={{
                  p: "10px 20px",
                }}
              >
                Explore public capsules
              </Button>
            </Stack>
          </Box>
        </Container>

        {(countError || configError) && (
          <Snackbar
            open={true}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity="warning" variant="filled">
              {countError || configError}
            </Alert>
          </Snackbar>
        )}
      </>
    );
  }

  return (
    <>
      <Backdrop
        sx={(t) => ({ color: t.colors.white, zIndex: t.zIndex.drawer + 1 })}
        open={loading}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing up...
          </Typography>
        </Box>
      </Backdrop>

      <Container
        maxWidth="xs"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          minHeight: "100%",
        })}
      >
        <Box sx={{ pt: 4, px: 4 }}>
          {/* Soft warning if we couldn’t verify capacity but gate isn’t triggered */}
          {(countError || configError) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              We’re having trouble verifying capacity right now. You may still
              try to sign up, but if capacity is reached, it will be blocked.
            </Alert>
          )}

          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              textAlign: "left",
              mb: 2,
            }}
          >
            Sign Up with Email Address
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                variant="outlined"
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": { borderColor: theme.colors.blue },
                    "&:hover fieldset": { borderColor: theme.colors.blue },
                    "&.Mui-focused fieldset": { borderColor: theme.colors.blue },
                  },
                })}
              />
              {email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
                <Typography color="error" fontSize="0.8rem" mt={0.5}>
                  Please enter a valid email address.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="Create Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        sx={(theme) => ({ color: theme.palette.text.secondary })}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": { borderColor: theme.colors.blue },
                    "&:hover fieldset": { borderColor: theme.colors.blue },
                    "&.Mui-focused fieldset": { borderColor: theme.colors.blue },
                  },
                })}
              />
              {password &&
                !/^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{6,})/.test(password) && (
                  <Typography color="error" fontSize="0.8rem" mt={0.5}>
                    Password must be at least 6 characters, include 1 uppercase
                    letter and 1 special character.
                  </Typography>
                )}
            </Grid>

            <Grid item xs={12}>
              <Button
                onClick={handleSignUp}
                variant="contained"
                disabled={
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
                  !/^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{6,})/.test(password) ||
                  loading
                }
                sx={(theme) => ({
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  padding: "14px 40px",
                  mb: "10px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                })}
              >
                Sign Up
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography align="left">
                Already a member?{" "}
                <Link
                  onClick={() =>
                    navigate("/signin", {
                      state: { from },
                    })
                  }
                  underline="hover"
                  sx={(theme) => ({
                    cursor: "pointer",
                    color: theme.colors.blue,
                  })}
                >
                  Sign in!
                </Link>
              </Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 12px)`,
            }}
          >
            <AppBuildFooter />
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUpPage;
